import { type RefObject } from 'react';
import type { EditorView } from 'prosemirror-view';
import prepareHtml from 'utils/prepareHtml';

export interface HandleMDPasteOptions {
  /**
   * Функция, которая будет вставлять готовый html в редактор.
   * Например: (html) => contentCommandRef.current?.insert(html)
   */
  insertHtml: (html: string) => void;
  shiftPressedRef?: RefObject<boolean>;
}

/**
 * Обработчик Paste для Markdown → HTML.
 *
 * 1. Проверяем, зажат ли Shift. Если Shift, то пропускаем (return false).
 * 2. Иначе получаем буфер обмена как text/plain.
 * 3. Конвертируем `prepareHtml(text)` => html
 * 4. Вызываем insertHtml(html)
 * 5. Возвращаем true, если действительно вставили.
 */
export function handleMDPaste(options: HandleMDPasteOptions) {
  return function (view: EditorView, event: ClipboardEvent): boolean {
    const { insertHtml, shiftPressedRef } = options;

    if (shiftPressedRef?.current) {
      return false;
    }

    const { clipboardData } = event;
    if (!clipboardData) {
      return false;
    }

    const text = clipboardData.getData('text/plain');
    if (!text) {
      return false;
    }

    const html = prepareHtml({ text }, { async: false, html: true });
    if (!html) {
      return false;
    }

    insertHtml(html);

    event.preventDefault();
    return true;
  };
}
